"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { api, type NowPlaying } from "@/lib/api";
import { useStation } from "@/lib/station-context";
import { TWEAKS_EVENT } from "@/components/dashboard/tweaks-card";

// Broadcast-signal icon for the ON AIR badge. When live, the concentric
// arcs pulse outward in sequence (staggered animation-delay per arc),
// reading as "actively transmitting"; when offline it's the same icon,
// still. Driven by real data, not decoration: the badge is live only
// while the API reports a playlist track actually playing
// (nowPlaying.on_air) — not merely because some track was once reported.
function SignalIcon({ live }: { live: boolean }) {
  // Two arcs on each side of a centre dot, pulsing outward horizontally
  // (inner arc first, then the outer one) — the broadcast mark from the design.
  const arc = (delay: string) => (live ? `animate-signal-arc [animation-delay:${delay}]` : "");
  return (
    <svg viewBox="0 0 28 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-6">
      <circle cx="14" cy="10" r="2.2" fill="currentColor" stroke="none" />
      <path className={arc("0ms")} d="M9.5 6.5a5.5 5.5 0 0 0 0 7" />
      <path className={arc("0ms")} d="M18.5 6.5a5.5 5.5 0 0 1 0 7" />
      <path className={arc("250ms")} d="M5.5 3.5a10 10 0 0 0 0 13" />
      <path className={arc("250ms")} d="M22.5 3.5a10 10 0 0 1 0 13" />
    </svg>
  );
}

// Always rendered: red "ON AIR" while Liquidsoap is playing the active
// playlist, greyed "OFFLINE" when it isn't (no active playlist, playlist
// finished, or the station is silent).
function OnAirBadge({ nowPlaying }: { nowPlaying: NowPlaying | null }) {
  const live = !!nowPlaying?.on_air;
  return (
    <div
      className={`flex items-center justify-center gap-1.5 text-xs ${
        live ? "bg-accent-red text-white" : "bg-slate-200 text-slate-500"
      }`}
      style={{ borderRadius: "4px", padding: "10px", fontWeight: 500, width: "104px" }}
      role="status"
      aria-label={live ? "On air" : "Offline"}
    >
      <SignalIcon live={live} />
      {live ? "ON AIR" : "OFFLINE"}
    </div>
  );
}

// Who is driving the station: a live DJ, or AutoDJ playing the playlists and
// schedule. Read from the same now-playing poll as the ON AIR badge.
function BroadcastStatus({ nowPlaying }: { nowPlaying: NowPlaying | null }) {
  const { station } = useStation();
  const stationId = station?.id ?? null;
  const [style, setStyle] = useState<"standard" | "pill">("standard");

  // The style is a Settings option; re-read it when the station changes or the
  // Settings page saves a new choice.
  useEffect(() => {
    let cancelled = false;
    function load() {
      api
        .getStationConfig()
        .then((c) => {
          if (!cancelled) setStyle(c.broadcast_status_style === "pill" ? "pill" : "standard");
        })
        .catch(() => {});
    }
    const first = setTimeout(load, 0);
    window.addEventListener(TWEAKS_EVENT, load);
    return () => {
      cancelled = true;
      clearTimeout(first);
      window.removeEventListener(TWEAKS_EVENT, load);
    };
  }, [stationId]);

  const onAir = !!nowPlaying?.on_air;
  const isLive = onAir && !!nowPlaying?.live;
  const djName = isLive ? (nowPlaying?.dj ?? "DJ") : null;

  if (style === "pill") {
    // A pill, like the ON AIR badge: red for Live, blue for Auto DJ, greyed out "None" when
    // off air (the DJ's name sits beside it as plain text). The dot blinks only on air.
    const tone = !onAir
      ? "bg-slate-200 text-slate-500"
      : isLive
        ? "bg-accent-red text-white"
        : "bg-accent-blue text-white";
    const dot = !onAir ? "bg-slate-400" : "bg-white animate-status-blink";
    const text = !onAir ? "None" : isLive ? "Live" : "Auto DJ";
    return (
      <div>
        <div className="mb-0.5 pl-1 text-[11px] uppercase tracking-wide text-text-tertiary">Broadcast Status</div>
        <div className="flex items-center gap-2" role="status">
          <div className={`flex items-center gap-1.5 rounded-[4px] px-3 py-1 text-[11px] font-semibold ${tone}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
            {text}
          </div>
          {djName && <span className="max-w-[200px] truncate text-[13px] font-semibold text-text-primary">{djName}</span>}
        </div>
      </div>
    );
  }

  // Standard: a blinking dot and text. Live is red ("Live · DJ Name"), Auto DJ blue,
  // off air grey. (The API Status uses the green dot.)
  const dotColor = !onAir ? "bg-slate-400" : isLive ? "bg-accent-red animate-status-blink" : "bg-accent-blue animate-status-blink";
  const textColor = !onAir ? "text-slate-500" : isLive ? "text-accent-red" : "text-text-primary";
  const label = !onAir ? "Off air" : isLive ? `Live · ${djName}` : "Auto DJ";
  return (
    <div>
      <div className="mb-0.5 pl-1 text-[11px] uppercase tracking-wide text-text-tertiary">Broadcast Status</div>
      <div className="flex items-center gap-2 pl-1" role="status">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotColor}`} />
        <span className={`max-w-[240px] truncate text-[13px] font-semibold ${textColor}`}>{label}</span>
      </div>
    </div>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The account's display name when it has one (set on the Users page);
  // otherwise the role, capitalized, which is the label the mockup itself
  // shows ("Admin"). The menu also shows the real email underneath.
  const roleLabel = user ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "…";
  const label = user?.name?.trim() || roleLabel;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-bg-main"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-main text-text-tertiary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
        </div>
        <div className="text-left">
          <div className="text-[10px] uppercase leading-tight tracking-wide text-text-tertiary">
            Welcome Back
          </div>
          <div className="max-w-[140px] truncate text-[13px] font-semibold leading-tight">{label}</div>
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-3.5 w-3.5 shrink-0 text-text-tertiary transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-10 w-56 rounded-xl border border-border-light bg-bg-card py-1.5 shadow-lg">
          {user && (
            <div className="border-b border-border-light px-3.5 py-2.5">
              <div className="truncate text-xs font-semibold text-text-primary">{label}</div>
              <div className="truncate text-xs text-text-tertiary">{user.email}</div>
              {user.name?.trim() && <div className="text-xs text-text-tertiary">{roleLabel}</div>}
            </div>
          )}
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-accent-red hover:bg-bg-main"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

export function Topbar({
  apiHealthy,
  nowPlaying,
}: {
  apiHealthy: boolean | null;
  nowPlaying: NowPlaying | null;
}) {
  return (
    <div className="flex items-center gap-6 border-b border-border-light bg-white px-7 py-3.5">
      <div>
        {/* pl-[14px] matches the dot (8px) + gap (6px) indent below, so
            this label sits directly over "Connected", not over the dot. */}
        <div className="pl-[14px] text-[11px] uppercase tracking-wide text-text-tertiary">
          API Status
        </div>
        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
          <span
            className={`relative top-[3px] h-2 w-2 rounded-full ${
              apiHealthy === null
                ? "bg-slate-300"
                : apiHealthy
                  ? "bg-accent-green animate-status-blink"
                  : "bg-accent-red"
            }`}
          />
          {apiHealthy === null ? "Checking…" : apiHealthy ? "Connected" : "Unreachable"}
        </div>
      </div>

      {/* Icecast/broadcast-level status (hostname, port, listener connection
          state, time-before-going-live) intentionally omitted here rather
          than shown with placeholder numbers — there's no status-aggregation
          endpoint built yet (see groundwork.md Section 8.3). This will be a
          real, wired-up section once that's built, not a cosmetic stub. */}

      <div className="h-9 w-px bg-border-light" aria-hidden="true" />

      <BroadcastStatus nowPlaying={nowPlaying} />

      <div className="flex-1" />

      <OnAirBadge nowPlaying={nowPlaying} />
      <AccountMenu />
    </div>
  );
}
